"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, get, set } from "firebase/database"
import { Save, RefreshCw } from "lucide-react"
import "./system-settings.css"

const SystemSettings = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [settings, setSettings] = useState({
    maintenanceMode: false,
    systemNotice: "",
    maxOrdersPerUser: 50,
    maxQuotationsPerDay: 20,
    orderCancellationPeriod: 24,
    enableUserRegistration: true,
    enableSupplierRegistration: false,
    enableNotifications: true,
    enableWhatsAppNotifications: false,
    whatsAppApiKey: "",
    whatsAppApiUrl: "",
  })

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true)
      try {
        const db = getDatabase()
        const settingsRef = ref(db, "systemSettings")
        const snapshot = await get(settingsRef)

        if (snapshot.exists()) {
          setSettings(snapshot.val())
        }
      } catch (error) {
        console.error("Error fetching settings:", error)
        setError("Failed to load system settings. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchSettings()
  }, [])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value,
    }))
  }

  const handleSaveSettings = async () => {
    setError("")
    setSuccess("")
    setIsLoading(true)

    try {
      const db = getDatabase()
      const settingsRef = ref(db, "systemSettings")
      await set(settingsRef, settings)
      setSuccess("System settings saved successfully!")
    } catch (error) {
      console.error("Error saving settings:", error)
      setError("Failed to save system settings. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetSettings = async () => {
    if (!window.confirm("Are you sure you want to reset all settings to default values?")) {
      return
    }

    setIsLoading(true)
    setError("")
    setSuccess("")

    try {
      const defaultSettings = {
        maintenanceMode: false,
        systemNotice: "",
        maxOrdersPerUser: 50,
        maxQuotationsPerDay: 20,
        orderCancellationPeriod: 24,
        enableUserRegistration: true,
        enableSupplierRegistration: false,
        enableNotifications: true,
        enableWhatsAppNotifications: false,
        whatsAppApiKey: "",
        whatsAppApiUrl: "",
      }

      const db = getDatabase()
      const settingsRef = ref(db, "systemSettings")
      await set(settingsRef, defaultSettings)

      setSettings(defaultSettings)
      setSuccess("System settings reset to default values!")
    } catch (error) {
      console.error("Error resetting settings:", error)
      setError("Failed to reset system settings. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="system-settings-container">
      <div className="system-settings-header">
        <h1>System Settings</h1>
        <div className="settings-actions">
          <button className="reset-settings-button" onClick={handleResetSettings} disabled={isLoading}>
            <RefreshCw size={16} /> Reset to Default
          </button>
          <button className="save-settings-button" onClick={handleSaveSettings} disabled={isLoading}>
            <Save size={16} /> Save Settings
          </button>
        </div>
      </div>

      {error && <div className="settings-error-message">{error}</div>}
      {success && <div className="settings-success-message">{success}</div>}

      {isLoading ? (
        <div className="settings-loading">Loading system settings...</div>
      ) : (
        <div className="settings-form-container">
          <div className="settings-section">
            <h2>General Settings</h2>
            <div className="settings-grid">
              <div className="settings-item">
                <div className="settings-item-header">
                  <label htmlFor="maintenanceMode" className="settings-label">
                    Maintenance Mode
                  </label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="maintenanceMode"
                      name="maintenanceMode"
                      checked={settings.maintenanceMode}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="settings-description">
                  When enabled, the system will be in maintenance mode and users will not be able to access it.
                </p>
              </div>

              <div className="settings-item">
                <label htmlFor="systemNotice" className="settings-label">
                  System Notice
                </label>
                <textarea
                  id="systemNotice"
                  name="systemNotice"
                  value={settings.systemNotice}
                  onChange={handleInputChange}
                  placeholder="Enter a system-wide notice to display to all users"
                  rows={3}
                  className="settings-textarea"
                />
                <p className="settings-description">
                  This message will be displayed to all users at the top of the dashboard.
                </p>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h2>User & Order Settings</h2>
            <div className="settings-grid">
              <div className="settings-item">
                <label htmlFor="maxOrdersPerUser" className="settings-label">
                  Max Orders Per User
                </label>
                <input
                  type="number"
                  id="maxOrdersPerUser"
                  name="maxOrdersPerUser"
                  value={settings.maxOrdersPerUser}
                  onChange={handleInputChange}
                  min="1"
                  max="1000"
                  className="settings-input"
                />
                <p className="settings-description">Maximum number of active orders a user can have at one time.</p>
              </div>

              <div className="settings-item">
                <label htmlFor="maxQuotationsPerDay" className="settings-label">
                  Max Quotations Per Day
                </label>
                <input
                  type="number"
                  id="maxQuotationsPerDay"
                  name="maxQuotationsPerDay"
                  value={settings.maxQuotationsPerDay}
                  onChange={handleInputChange}
                  min="1"
                  max="100"
                  className="settings-input"
                />
                <p className="settings-description">Maximum number of quotation requests a user can submit per day.</p>
              </div>

              <div className="settings-item">
                <label htmlFor="orderCancellationPeriod" className="settings-label">
                  Order Cancellation Period (hours)
                </label>
                <input
                  type="number"
                  id="orderCancellationPeriod"
                  name="orderCancellationPeriod"
                  value={settings.orderCancellationPeriod}
                  onChange={handleInputChange}
                  min="1"
                  max="72"
                  className="settings-input"
                />
                <p className="settings-description">
                  Time period (in hours) during which users can cancel their orders.
                </p>
              </div>

              <div className="settings-item">
                <div className="settings-item-header">
                  <label htmlFor="enableUserRegistration" className="settings-label">
                    Enable User Registration
                  </label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="enableUserRegistration"
                      name="enableUserRegistration"
                      checked={settings.enableUserRegistration}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="settings-description">
                  When disabled, new users will not be able to register on the platform.
                </p>
              </div>

              <div className="settings-item">
                <div className="settings-item-header">
                  <label htmlFor="enableSupplierRegistration" className="settings-label">
                    Enable Supplier Registration
                  </label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="enableSupplierRegistration"
                      name="enableSupplierRegistration"
                      checked={settings.enableSupplierRegistration}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="settings-description">
                  When enabled, suppliers can self-register. Otherwise, only admins can create supplier accounts.
                </p>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h2>Notification Settings</h2>
            <div className="settings-grid">
              <div className="settings-item">
                <div className="settings-item-header">
                  <label htmlFor="enableNotifications" className="settings-label">
                    Enable In-App Notifications
                  </label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="enableNotifications"
                      name="enableNotifications"
                      checked={settings.enableNotifications}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="settings-description">Enable or disable in-app notifications for all users.</p>
              </div>

              <div className="settings-item">
                <div className="settings-item-header">
                  <label htmlFor="enableWhatsAppNotifications" className="settings-label">
                    Enable WhatsApp Notifications
                  </label>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="enableWhatsAppNotifications"
                      name="enableWhatsAppNotifications"
                      checked={settings.enableWhatsAppNotifications}
                      onChange={handleInputChange}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="settings-description">Enable or disable WhatsApp notifications for all users.</p>
              </div>

              <div className="settings-item">
                <label htmlFor="whatsAppApiKey" className="settings-label">
                  WhatsApp API Key
                </label>
                <input
                  type="text"
                  id="whatsAppApiKey"
                  name="whatsAppApiKey"
                  value={settings.whatsAppApiKey}
                  onChange={handleInputChange}
                  placeholder="Enter WhatsApp API Key"
                  className="settings-input"
                />
                <p className="settings-description">API key for WhatsApp integration.</p>
              </div>

              <div className="settings-item">
                <label htmlFor="whatsAppApiUrl" className="settings-label">
                  WhatsApp API URL
                </label>
                <input
                  type="text"
                  id="whatsAppApiUrl"
                  name="whatsAppApiUrl"
                  value={settings.whatsAppApiUrl}
                  onChange={handleInputChange}
                  placeholder="Enter WhatsApp API URL"
                  className="settings-input"
                />
                <p className="settings-description">API URL for WhatsApp integration.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SystemSettings;

